// src/components/PartnerConfirmModal.js
import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import styles from './PartnerConfirmModal.module.css'; // Reuse product modal styles for consistency
import Spinner from "./Spinner";

const PartnerConfirmModal = ({ isOpen, onClose, onConfirm, comparisonData, influencerName, confirmButtonText = 'Confirm & Save' }) => {
    const [finalData, setFinalData] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen && comparisonData) {
            // Initialize the editable 'finalData' state.
            // Prioritize user input, fall back to AI, then to an empty string.
            const { userInput, aiOutput } = comparisonData;
            setFinalData({
                nickname: aiOutput?.nickname || '',
                promoCode: userInput?.promoCode || aiOutput?.promoCode || '',
                emailPromoCode: aiOutput?.emailPromoCode || '',
                commissionRate: userInput?.commissionRate || aiOutput?.commissionRate || '',
                commissionValue: userInput?.commissionValue || aiOutput?.commissionValue || '', // <-- NEW
                commissionCloudRate: userInput?.commissionCloudRate || aiOutput?.commissionCloudRate || '', // <-- NEW
                discountConsumerRate: userInput?.discountConsumerRate || aiOutput?.discountConsumerRate || '',
                approvedProductNames: userInput?.approvedProductNames || aiOutput?.approvedProductNames || [],
            });
        }
    }, [isOpen, comparisonData]);

    const handleFieldChange = (field, value) => {
        setFinalData(prev => ({ ...prev, [field]: value }));
    };

    const handleConfirmClick = async () => {
        setIsSaving(true);
        await onConfirm(finalData);
        setIsSaving(false);
    };

    if (!isOpen) return null;

    const { userInput, aiOutput } = comparisonData;

    const renderComparisonField = (fieldName, label) => {
        const userValue = userInput?.[fieldName] ?? 'N/A';
        const aiValue = aiOutput?.[fieldName] ?? 'N/A';
        const isDiscrepancy = JSON.stringify(userValue) !== JSON.stringify(aiValue);

        return (
            <div className={styles.comparisonRow}>
                <span className={styles.comparisonLabel}>{label}:</span>
                <div className={styles.comparisonValues}>
                    <p><strong>User Input:</strong> {Array.isArray(userValue) ? userValue.join(', ') : userValue}</p>
                    <p className={isDiscrepancy ? styles.discrepancy : ''}>
                        <strong>AI Found:</strong> {Array.isArray(aiValue) ? aiValue.join(', ') : aiValue}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={styles.modalContent}
            overlayClassName={styles.modalOverlay}
            contentLabel="Confirm Partner Data"
        >
            <h2>Confirm Partner Data for {influencerName}</h2>
            <p>Review the data below. Edit the "Final Value" fields if necessary, then confirm to save.</p>
            
            {isSaving && <Spinner message="Saving..." />}

            <div className={`${styles.modalBody} ${styles.confirmationBody}`}>
                {/* --- Comparison Section --- */}
                {renderComparisonField('promoCode', 'Promo Code')}
                {renderComparisonField('commissionRate', 'Commission (%)')}
                {renderComparisonField('commissionValue', 'One-time fee for Content ($)')} {/* <-- NEW */}
                {renderComparisonField('commissionCloudRate', 'Cloud one-time Commission (%)')} {/* <-- NEW */}
                {renderComparisonField('discountConsumerRate', 'Customer Discount (%)')}
                {renderComparisonField('approvedProductNames', 'List of Plugins')}
                <hr className={styles.divider} />

                {/* --- Editable Final Values Section --- */}
                <div className={styles.formGroup}>
                    <label>Final Nickname:</label>
                    <input type="text" value={finalData.nickname || ''} onChange={(e) => handleFieldChange('nickname', e.target.value)} disabled={isSaving} />
                </div>
                <div className={styles.formGroup}>
                    <label>Final Promo Code:</label>
                    <input type="text" value={finalData.promoCode || ''} onChange={(e) => handleFieldChange('promoCode', e.target.value)} disabled={isSaving} />
                </div>
                <div className={styles.formGroup}>
                    <label>Final Commission Rate (%):</label>
                    <input type="text" value={finalData.commissionRate || ''} onChange={(e) => handleFieldChange('commissionRate', e.target.value)} disabled={isSaving} />
                </div>
                <div className={styles.formGroup}>
                    <label>Final One-time fee for Content ($):</label>
                    <input type="text" value={finalData.commissionValue || ''} onChange={(e) => handleFieldChange('commissionValue', e.target.value)} disabled={isSaving} />
                </div>
                <div className={styles.formGroup}>
                    <label>Final Cloud one-time Commission (%):</label>
                    <input type="text" value={finalData.commissionCloudRate || ''} onChange={(e) => handleFieldChange('commissionCloudRate', e.target.value)} disabled={isSaving} />
                </div>
                <div className={styles.formGroup}>
                    <label>Final Customer Discount (%):</label>
                    <input type="text" value={finalData.discountConsumerRate || ''} onChange={(e) => handleFieldChange('discountConsumerRate', e.target.value)} disabled={isSaving} />
                </div>
                 <div className={styles.formGroup}>
                    <label>Final Account Email:</label>
                    <input type="text" value={finalData.emailPromoCode || ''} onChange={(e) => handleFieldChange('emailPromoCode', e.target.value)} disabled={isSaving} />
                </div>
                <div className={styles.formGroup}>
                    <label>Final Approved Plugins (comma-separated):</label>
                    <textarea value={Array.isArray(finalData.approvedProductNames) ? finalData.approvedProductNames.join(', ') : ''} onChange={(e) => handleFieldChange('approvedProductNames', e.target.value.split(',').map(s => s.trim()))} rows="3" disabled={isSaving} />
                </div>
            </div>

            <div className={styles.modalActions}>
                <button onClick={onClose} className={`${styles.actionButton} ${styles.closeButton}`} disabled={isSaving}>Cancel</button>
                <button onClick={handleConfirmClick} className={`${styles.actionButton} ${styles.saveButton}`} disabled={isSaving}>
                    {isSaving ? 'Saving...' : confirmButtonText}
                </button>
            </div>
        </Modal>
    );
};
export default PartnerConfirmModal;